import cookieGroups from './data/cookieGroups';
import setCookie, { defaultExpiration } from './utils/setCookie';

/**
 * A custom element for enhancing a static cookies message
 * into one that lets a user update their cookie preferences on the same page.
 *
 * @module iabbb-cookies-element
 */
class CookiesMessage extends HTMLElement {
  constructor() {
    super();

    this.render = () => {
      this.enhanceAcceptAllCookies();

      // Only do the following if the browser supports the dialog element.
      // Otherwise, fall back to linking to manage cookies functionality.
      if (typeof HTMLDialogElement === 'function') {
        this.createPolicyDialog();
        this.replaceManageCookiesLinkWithButton();
      }

      this.addScrollPadding();
    };
  }

  connectedCallback() {
    this.render();
  }

  createPolicyDialog = () => {
    const dialog = document.createElement('dialog');
    dialog.tabIndex = -1;

    // 👇 wrapper div aids in light dismiss
    const dialogContent = document.createElement('div');

    const heading = document.createElement('h2');
    heading.className = 'bds-h2';
    heading.innerText = 'Cookie Preferences';

    const paragraph = document.createElement('p');
    paragraph.innerText = `Many websites use cookies or similar tools to store information on your browser or device. We use cookies
    on BBB websites to remember your preferences, improve website performance and enhance user experience, and
    to recommend content we believe will be most relevant to you. Most cookies collect anonymous information
    such as how users arrive at and use the website. Some cookies are necessary to allow the website to
    function properly, but you may choose to not allow other types of cookies below.`;

    dialogContent.appendChild(heading);
    dialogContent.appendChild(paragraph);

    const form = document.createElement('form');
    form.action = '/manage-cookies';
    form.method = 'post';
    form.onsubmit = (e) => {
      if ('FormData' in window) {
        e.preventDefault();

        const formValues = Object.fromEntries(new FormData(e.target));

        this.setCookiesPolicy({
          necessary: true,
          functional: formValues.functional === 'true',
          performance: formValues.performance === 'true',
          marketing: formValues.marketing === 'true',
        });

        this.setCookiesPreferencesSet();

        this.querySelector('dialog').close();
        this.showSavedPreferencesMessage();
      }
    };

    for (const cookieGroup of cookieGroups) {
      const group = this.createGroup(cookieGroup.name, cookieGroup.description, cookieGroup.isReadOnly);
      form.appendChild(group);
    }

    const actions = document.createElement('div');
    actions.className = 'actions';

    const saveChangesButton = document.createElement('button');
    saveChangesButton.innerText = 'Save Changes';
    saveChangesButton.className = 'bds-button';

    const acceptAllButton = document.createElement('button');
    acceptAllButton.className = 'bds-button-unstyled';
    acceptAllButton.name = 'allow-all';
    acceptAllButton.value = 'true';
    acceptAllButton.type = 'button';
    acceptAllButton.innerText = 'Accept All Cookies';

    acceptAllButton.onclick = () => {
      this.setAcceptAllCookiesPolicy();
      this.setCookiesPreferencesSet();

      this.querySelector('dialog').close();
      this.showSavedPreferencesMessage();
    };

    actions.appendChild(saveChangesButton);
    actions.appendChild(acceptAllButton);

    form.appendChild(actions);

    dialogContent.appendChild(form);

    dialog.appendChild(dialogContent);

    dialog.addEventListener('click', ({ target: dialog }) => {
      if (dialog.nodeName === 'DIALOG') {
        dialog.close('dismiss');
      }
    });

    this.appendChild(dialog);
  };

  replaceManageCookiesLinkWithButton = () => {
    const link = this.querySelector('.actions a');

    // If `link` is not set, we can assume that this replacement has already happened.
    if (!link) {
      return;
    }

    const btn = document.createElement('button');
    btn.innerHTML = link.innerHTML;
    btn.className = `${link.className || ''} bds-button-unstyled`;
    btn.onclick = () => {
      this.querySelector('dialog').showModal();
      this.querySelector('dialog').focus();
    };
    link.replaceWith(btn);
  };

  createGroup = (name, description, isReadOnly = false) => {
    const id = name.replace(' ', '').replace('Cookies', '').toLowerCase();

    const container = document.createElement('div');
    container.className = 'cookies-group';

    const heading = document.createElement('h3');
    heading.className = 'bds-h4';
    heading.id = `${id}-heading`;
    heading.innerText = name;

    const details = document.createElement('details');

    const summary = document.createElement('summary');
    summary.innerText = `What are ${name.toLowerCase()}?`;

    const detailsFull = document.createElement('div');
    detailsFull.innerText = description;

    details.appendChild(summary);
    details.appendChild(detailsFull);

    container.appendChild(heading);
    container.appendChild(details);

    if (isReadOnly) {
      const disclaimer = document.createElement('p');
      disclaimer.innerText = `${name} must always be enabled`;
      container.appendChild(disclaimer);
    } else {
      const fields = document.createElement('fieldset');
      fields.setAttribute('aria-labelledby', `${id}-heading`);

      const iconRadio = `<svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <circle
          class="background"
          cx="12"
          cy="12"
          r="11.5"
          stroke="currentColor"
          fill="none"
          stroke-width="1"
          vector-effect="non-scaling-stroke"
        />
        <circle class="radio" cx="12" cy="12" fill="currentColor" />
      </svg>`;

      const radioContainerTrue = document.createElement('div');
      radioContainerTrue.className = 'bds-form-group';
      radioContainerTrue.innerHTML = `
        <div class="bds-radio" data-enhanced="true">
          <input type="radio" id="${id}-input-0" name="${id}" value="true" checked="" />
          ${iconRadio}
          <label for="${id}-input-0">Use ${name.toLowerCase()}</label>
        </div>
      `;

      const radioContainerFalse = document.createElement('div');
      radioContainerFalse.className = 'bds-form-group';
      radioContainerFalse.innerHTML = `
        <div class="bds-radio" data-enhanced="true">
          <input type="radio" id="${id}-input-1" name="${id}" value="false" />
          ${iconRadio}
          <label for="${id}-input-1">Do not use ${name.toLowerCase()}</label>
        </div>
      `;

      fields.appendChild(radioContainerTrue);
      fields.appendChild(radioContainerFalse);
      container.appendChild(fields);
    }

    return container;
  };

  enhanceAcceptAllCookies = () => {
    this.querySelector('form').addEventListener('submit', (e) => {
      e.preventDefault();

      this.setAcceptAllCookiesPolicy();
      this.setCookiesPreferencesSet();

      this.showSavedPreferencesMessage();
    });
  };

  setCookiesPolicy = (cookiesPolicy) => {
    setCookie('iabbb_cookies_policy', JSON.stringify(cookiesPolicy), defaultExpiration);
  };

  setAcceptAllCookiesPolicy = () => {
    const allCookiesPolicy = {
      necessary: true,
      functional: true,
      performance: true,
      marketing: true,
    };

    this.setCookiesPolicy(allCookiesPolicy);
  };

  setCookiesPreferencesSet = () => {
    setCookie('iabbb_cookies_preferences_set', 'true', defaultExpiration);
  };

  showSavedPreferencesMessage = () => {
    const message = this.querySelector('.layout p');

    message.innerHTML = `Your cookie settings have been saved. You can <a href="${window.headerFooterData.baseUrl}/manage-cookies">change your cookie settings</a> at any time.`;
    message.tabIndex = -1;

    const hideThisMessageButton = document.createElement('button');
    hideThisMessageButton.innerText = 'Hide This Message';
    hideThisMessageButton.className = 'bds-button';
    hideThisMessageButton.onclick = () => {
      this.innerHTML = '';
    };

    this.querySelector('.layout .actions').innerHTML = '';
    this.querySelector('.layout .actions').appendChild(hideThisMessageButton);

    message.focus();
  };

  // Add scroll padding to prevent focused elements from being obscured by this message.
  // Credit: https://codepen.io/TPG/pen/QWVMXMg
  // Changed: Added overflow: auto to inline styles, as scroll-padding was otherwise not taking effect
  // Changed: references to header were switched to cookies message container, and scroll-padding-top was switched to scroll-padding-bottom
  addScrollPadding = () => {
    // Config: specify a limiting media query
    // n.b. this is so small-screen/high-zoom layouts can un-stick the header
    // in which case, scroll-padding is unnecessary and wastes valuable space
    // so it will only be applied if the page matches this media query
    const MEDIA_MATCH = '(min-width: 20em)';

    // Config: specify extra padding to allow for focus outlines
    // n.b. this avoids the possibility of the outline being slightly clipped,
    // when a focused element is close to the threshold, since scroll-padding
    // applies to the layout box, and outline isn't part of the layout box
    const EXTRA_PADDING = 6;

    //create a style node and get its stylesheet reference
    const style = document.head.appendChild(document.createElement('style'));

    const scrollFocusedElementIntoView = (height) => {
      // if an element outside the cookies message is currently focused,
      // and the change in height has caused it to become obscured,
      // then scroll it into view (as though the user had tabbed there)
      // n.b. this prevents active focus being obscured on the fly
      // but is not technically required for 2.4.11 conformance
      const focused = document.activeElement || document.body;
      if (focused !== document.body && !this.contains(focused)) {
        if (focused.getBoundingClientRect().top < height) {
          focused.scrollIntoView();
        }
      }
    };

    const applyScrollPadding = () => {
      if (!style) {
        return;
      }

      // get the cookies message height, and add a little extra as specified
      const height = this.offsetHeight + EXTRA_PADDING;

      // create a new rule for scroll padding from the cookies message height
      // filtered using :has() if that's supported
      // so that it doesn't apply to elements inside the cookies message
      // n.b. it's fine to use pixels, because any change in height
      // caused by text resizing will trigger the resize observer
      if ('CSS' in window && CSS.supports('selector(:has(*))')) {
        style.innerHTML = `html:has(:not(.cookies-message *, .cookies-policy-dialog *):focus)  { scroll-padding-bottom:${height}px; overflow: auto; }`;
      } else {
        style.innerHTML = `html { scroll-padding-bottom:${height}px; overflow: auto; }`;
      }

      scrollFocusedElementIntoView(height);
    };

    if ('ResizeObserver' in window) {
      // define a resize observer to monitor the size of the header
      // n.b. this fires on first observation, then for anything that resizes
      // the elements, eg. font size, text spacing, window size or orientation
      const observer = new ResizeObserver(applyScrollPadding);

      // function: enable resize monitor and apply scroll-padding
      // n.b. this is abstracted so we can turn it on and off
      // in response to changes in the media query match status
      const enableResizeMonitor = () => {
        observer.observe(this);
      };

      // function: disable resize monitor and remove applied scroll-padding
      const disableResizeMonitor = () => {
        if (!style) {
          return;
        }

        observer.unobserve(this);
        style.innerHTML = '';
      };

      // pass the limiting media query to matchMedia to get the initial match
      // then define a change listener to update this when the match changes
      // and either enable or disable the resize monitor accordingly
      const query = window.matchMedia(MEDIA_MATCH);
      query.addEventListener('change', (e) => {
        if (e.matches) {
          enableResizeMonitor();
        } else {
          disableResizeMonitor();
        }
      });

      // if we already match the current media, enable the monitor
      if (query.matches) {
        enableResizeMonitor();
      }
    }
  };
}

customElements.define('iabbb-cookies-message', CookiesMessage);
